import React, { useState } from 'react';

import { ScreenHead, Rise } from '../ui/screen-parts.js';
import { Chip, Modal, useFlash, useModal } from '../ui/kit.js';
import { useLast } from '../ui/actions/parts.js';

import LobbyRenovations from "../media/images/news-screen/lobby-renovations.webp";
import ElevatorMaintenance from "../media/images/news-screen/elevator-maintenance.webp";
import FireSafetySeminar from "../media/images/news-screen/fire-safety-seminar.webp";
import CommunityGardenEvent from "../media/images/news-screen/community-garden-event.webp";
import PowerOutage from "../media/images/news-screen/power-outage.webp";
import ParkingGarageMaintenance from "../media/images/news-screen/parking-garage-maintenance.webp";

/* `when` and `where` are what a reminder and a calendar entry need;
   `starts` is the day the reminder is for. Exported so the welcome
   screen's notice can open the same article. */
export const ARTICLES = [
    {
        title: "Lobby Renovations",
        date: "Oct 3, 2024",
        kind: 'Construction',
        when: 'Oct 10 to end of November, weekdays 8 AM to 6 PM', where: 'Main lobby; use the west wing entrance', starts: 'Oct 10',
        content: "The building’s lobby renovations will begin on October 10, 2024, and will focus on improving the overall aesthetics and functionality. The renovation will include new flooring, modern seating areas, and improved lighting. During this time, the lobby will be temporarily closed, and an alternate entrance will be provided through the west wing. Construction hours will be from 8 AM to 6 PM on weekdays, and the project is expected to be completed by the end of November.",
        imageUrl: LobbyRenovations
    },
    {
        title: "Maintenance Work on Elevators",
        date: "Sep 23, 2024",
        kind: 'Maintenance',
        when: 'Oct 1 to Oct 5', where: 'Passenger elevators; one stays in service', starts: 'Oct 1',
        content: "Starting on October 1, 2024, routine maintenance will be performed on all building elevators. One elevator will remain in operation while the other undergoes repairs. We apologize for any inconvenience this may cause and appreciate your understanding. The maintenance is expected to last until October 5, 2024.",
        imageUrl: ElevatorMaintenance
    },
    {
        title: "Fire Safety Seminar",
        date: "Sep 11, 2024",
        kind: 'Community',
        when: 'Sep 18, 6 PM', where: 'Community room', starts: 'Sep 18',
        content: "A fire safety seminar will be held on September 18, 2024, at 6 PM in the community room. All residents are encouraged to attend to learn about the building's fire safety protocols and how to respond in the event of an emergency. Refreshments will be provided. Please RSVP at the front desk if you plan to attend.",
        imageUrl: FireSafetySeminar
    },
    {
        title: "Community Garden Event",
        date: "Aug 29, 2024",
        kind: 'Community',
        when: 'Sep 5', where: 'Community garden, level 3 terrace', starts: 'Sep 5',
        content: "Join us on September 5, 2024, for the community garden event, where all residents are invited to participate in planting new flowers and maintaining the garden. This is a great opportunity to meet your neighbors and contribute to the beautification of our shared spaces. Tools and refreshments will be provided.",
        imageUrl: CommunityGardenEvent
    },
    {
        title: "Power Outage Notice",
        date: "Aug 15, 2024",
        kind: 'Notice',
        when: 'Aug 20, 10 AM to 12 PM', where: 'Entire building', starts: 'Aug 20',
        content: "A brief power outage will affect the entire building on August 20, 2024, from 10 AM to 12 PM due to scheduled electrical maintenance. Please make necessary preparations and notify the front desk if you require any special assistance during this time.",
        imageUrl: PowerOutage
    },
    {
        title: "Parking Garage Maintenance",
        date: "Aug 1, 2024",
        kind: 'Maintenance',
        when: 'Aug 5 to Aug 12', where: 'Parking garage; some sections closed', starts: 'Aug 5',
        content: "Starting on August 5, 2024, the parking garage will undergo routine maintenance, including cleaning, repainting, and lighting upgrades. The garage will remain open, but certain sections will be closed off during the maintenance. This work is expected to continue until August 12, 2024.",
        imageUrl: ParkingGarageMaintenance
    }
];

/* Reminders and calendar entries, by article title. They cross screens:
   one set from the welcome screen's notice shows on the news card too,
   so they live at module level rather than in either screen's state. */
let SAVED = {};

export function useSavedArticles(notify) {
    const [saved, setSaved] = useState(SAVED);

    const mark = (article, key) => {
        const on = !SAVED[article.title]?.[key];
        SAVED = { ...SAVED, [article.title]: { ...SAVED[article.title], [key]: on } };
        setSaved(SAVED);
        if (key === 'remind') {
            notify(on
                ? `Reminder set. We will text you the day before ${article.starts}, and again that morning.`
                : `Reminder for ${article.title} removed.`);
        } else {
            notify(on
                ? `${article.title} added to your calendar: ${article.when}.`
                : `${article.title} removed from your calendar.`);
        }
    };

    return [saved, mark];
}

function NewsScreen()
{
    const [flash, notify] = useFlash();
    const [saved, mark] = useSavedArticles(notify);
    const sheet = useModal();

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Building news" title="What's happening here" />
            {flash}

            <div className="news-scroll">
                {ARTICLES.map((article, index) => {
                    const state = saved[article.title] || {};
                    return (
                        <Rise className="act-cell" key={article.title} index={index}>
                            <button
                                type="button"
                                className="news-card act-card news-card--open"
                                onClick={() => sheet.open(article)}
                                aria-label={`${article.title}, ${article.date}. Read the full article`}
                            >
                                <img src={article.imageUrl} alt="" className="news-image" loading="lazy" />
                                <span className="news-card__top">
                                    <span className="news-card__date">{article.date}</span>
                                    {state.remind && <Chip tone="ok">Reminder set</Chip>}
                                    {state.calendar && !state.remind && <Chip tone="info">In your calendar</Chip>}
                                </span>
                                <h3>{article.title}</h3>
                                <span className="news-card__body">
                                    <p>{article.content}</p>
                                </span>
                                <span className="act-more">Read article</span>
                            </button>
                        </Rise>
                    );
                })}
            </div>

            <ArticleSheet item={sheet.item} saved={sheet.item ? saved[sheet.item.title] || {} : {}} onClose={sheet.close} onMark={mark} />
        </div>
    );
}

/* The whole article, with the two things a reader does with a notice:
   be reminded of it, and put it in the calendar. Exported for the
   welcome screen's notice, which opens the lobby article. */
export function ArticleSheet({ item, saved = {}, onClose, onMark, extra }) {
    const shown = useLast(item);
    if (!shown) return <Modal open={false} onClose={onClose} />;
    const a = shown;

    return (
        <Modal
            open={Boolean(item)}
            onClose={onClose}
            size="lg"
            eyebrow={`Building news · ${a.date}`}
            title={a.title}
            actions={
                <>
                    {extra}
                    <button type="button" className={`btn ${saved.calendar ? 'btn-primary' : 'btn-outline-light'}`} aria-pressed={Boolean(saved.calendar)} onClick={() => onMark(a, 'calendar')}>
                        {saved.calendar ? 'In your calendar' : 'Add to calendar'}
                    </button>
                    <button type="button" className={`btn ${saved.remind ? 'btn-primary' : 'btn-outline-light'}`} aria-pressed={Boolean(saved.remind)} onClick={() => onMark(a, 'remind')}>
                        {saved.remind ? 'Reminder set' : 'Remind me'}
                    </button>
                </>
            }
        >
            <img className="modal__media article__photo" src={a.imageUrl} alt="" />
            <div className="row__end" style={{ justifyContent: 'flex-start' }}>
                <Chip tone="info">{a.kind}</Chip>
                {saved.remind && <Chip tone="ok">Reminder set</Chip>}
                {saved.calendar && <Chip tone="ok">In your calendar</Chip>}
            </div>
            <dl className="act-facts">
                <div className="act-facts__wide"><dt>When</dt><dd>{a.when}</dd></div>
                <div className="act-facts__wide"><dt>Where</dt><dd>{a.where}</dd></div>
            </dl>
            <p className="article__body">{a.content}</p>
            <p className="row__meta">Questions? The front desk is staffed around the clock at extension 0.</p>
        </Modal>
    );
}

export default NewsScreen;
